import React, { useCallback, useEffect, useState } from "react";
import { Button, Group, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import AccountingReportFilters from "./accounting/AccountingReportFilters";
import TrialBalanceView from "./accounting/TrialBalanceView";
import { downloadTrialBalanceCsv } from "./accounting/trialBalanceUtils";

const ReportTrialBalance = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const [fiscalPeriodId, setFiscalPeriodId] = useState(null);
  const [periodName, setPeriodName] = useState("");
  const [asOfDate, setAsOfDate] = useState(null);
  const [accountTypes, setAccountTypes] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const loadReport = async () => {
    setLoading(true);
    try {
      const query = {};
      if (fiscalPeriodId) query.fiscalPeriodId = fiscalPeriodId;
      if (asOfDate) query.endDate = asOfDate.toISOString();
      const data = await api.Accounting.getTrialBalance(query);
      setReport(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fiscalPeriodId) return;
    const loadPeriodName = async () => {
      try {
        const period = await api.getById({
          apiEntity: "fiscalPeriod",
          id: fiscalPeriodId,
        });
        setPeriodName(period?.name || "");
      } catch (error) {
        setPeriodName("");
      }
    };
    loadPeriodName();
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPeriodId]);

  const handleDownload = useCallback(() => {
    if (!report || filteredRows.length === 0) {
      showNotification({
        color: "yellow",
        title: t("accounting.trialBalance.downloadFailed"),
        message: t("accounting.trialBalance.noActivity"),
      });
      return;
    }
    downloadTrialBalanceCsv({
      rows: filteredRows,
      report,
      periodName,
      asOfDate,
      t,
    });
  }, [asOfDate, filteredRows, periodName, report, t]);

  return (
    <div>
      <Text size="xs" color="dimmed" mb="xs">
        {t("accounting.trialBalance.pageIntro")}
      </Text>
      <AccountingReportFilters
        fiscalPeriodId={fiscalPeriodId}
        onFiscalPeriodChange={setFiscalPeriodId}
        asOfDate={asOfDate}
        onAsOfDateChange={setAsOfDate}
        showAsOfDate
        showAccountTypeFilter
        accountTypes={accountTypes}
        onAccountTypesChange={setAccountTypes}
      />
      <Group mb="sm">
        <Button
          size="xs"
          variant="default"
          onClick={handleDownload}
          disabled={!report || filteredRows.length === 0}
        >
          {t("Download")}
        </Button>
        <Button size="xs" onClick={loadReport} loading={loading}>
          {t("Run Report")}
        </Button>
      </Group>
      <TrialBalanceView
        report={report}
        loading={loading}
        accountTypes={accountTypes}
        onFilteredRowsChange={setFilteredRows}
      />
    </div>
  );
};

export default ReportTrialBalance;
